import type { ReactNode } from 'react'
import { ErrorAlert } from '@/components/ErrorAlert'
import { UNEXPECTED_ERROR_COPY, RATE_LIMITED_COPY, WAF_CHALLENGE_COPY } from '@/copy/registry'
import type { Problem } from '@/data/apiClient'

/**
 * Shared across the auth family's 17 screens: the two refusal renderings that do not vary by
 * screen. §33.6's stated error state (INTERMAL_ERROR / UPSTREAM_UNAVAILABLE — never server
 * detail) and RefusalKind.yaml #36's waiting state (RATE_LIMITED — the server's own retry value,
 * never an invented interval). Not under src/components/: these are screen-composition helpers,
 * not catalogued components in their own right.
 */
export function UnexpectedRefusal({ problem }: { problem: Problem }) {
  return <ErrorAlert title={UNEXPECTED_ERROR_COPY.title} detail={UNEXPECTED_ERROR_COPY.detailTemplate(problem.correlation_id)} />
}

export function RateLimitedNotice({ problem }: { problem: Problem }) {
  return <p className="text-fail text-body">{RATE_LIMITED_COPY.waitingTemplate(problem.retry_after_seconds ?? 0)}</p>
}

/** `WafChallengeError`'s rendering — OI-C50's interim fallback, see that error's own doc comment. */
export function WafChallengeNotice() {
  return <p className="text-fail text-body">{WAF_CHALLENGE_COPY.message}</p>
}

export function FormError({ children }: { children: ReactNode }) {
  return <p className="text-fail text-body">{children}</p>
}

/** True for the two refusal kinds every screen's own switch should fall through to `Unexpected`
 * on. Kept as a helper so no screen re-lists the two kind strings. */
export function isUnexpectedRefusal(kind: string): boolean {
  return kind === 'INTERNAL_ERROR' || kind === 'UPSTREAM_UNAVAILABLE'
}
